import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { StayPlanUsage } from "@/hooks/useStayPlanContracts";

export type StayDayStatus = "scheduled" | "checked_in" | "checked_out" | "cancelled" | "no_show";

export interface PlanUsageDetail {
  stayReservationId: string;
  stayDate: string;
  status: StayDayStatus;
  /** `full` | `morning` | `afternoon`, ou `null` quando não foi definido. */
  period: string | null;
  checkInTime: string | null;
  checkOutTime: string | null;
  /** Nome da matilha da reserva, quando o pet está em uma. */
  packName: string | null;
  isOvernight: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DayRow = any;

export function normalizePlanUsageDetail(row: DayRow): PlanUsageDetail {
  return {
    stayReservationId: row.stay_reservation_id,
    stayDate: row.stay_date,
    status: (row.status === "scheduled" ? "scheduled" : row.status) as StayDayStatus,
    period: row.period ?? null,
    checkInTime: row.check_in_time ? String(row.check_in_time).slice(0, 5) : null,
    checkOutTime: row.check_out_time ? String(row.check_out_time).slice(0, 5) : null,
    packName: row.reservation?.pack?.name ?? null,
    isOvernight: Boolean(row.reservation?.is_overnight),
  };
}

/** A chave de um uso: uma reserva pode cobrir vários dias, cada um com o seu. */
export const planUsageDetailKey = (stayReservationId: string, stayDate: string) =>
  `${stayReservationId}|${stayDate}`;

export const planUsageDetailsKey = (contractId: string | null, usageIds: string[]): unknown[] => [
  "plan-usage-details",
  contractId,
  [...usageIds].sort(),
];

const DETAIL_SELECT =
  "stay_reservation_id, stay_date, status, period, check_in_time, check_out_time, " +
  "reservation:stay_reservations(is_overnight, pack:stay_packs(name))";

/**
 * O detalhe de cada diária do plano: turno, horários, matilha.
 *
 * Consulta `stay_reservation_days` diretamente em vez de reusar
 * `useStaySchedule`, que filtra `status = 'reserved'` e por isso devolveria
 * nada justamente para as diárias já realizadas — que são a maioria do que a
 * timeline mostra.
 */
export function usePlanUsageDetails(
  contractId: string | null,
  usages: StayPlanUsage[],
): UseQueryResult<Map<string, PlanUsageDetail>> {
  const reservationIds = [...new Set(usages.map((usage) => usage.stayReservationId).filter(Boolean))];
  const dates = [...new Set(usages.map((usage) => usage.usedOn))];

  return useQuery<Map<string, PlanUsageDetail>>({
    queryKey: planUsageDetailsKey(contractId, usages.map((usage) => usage.id)),
    enabled: Boolean(contractId) && reservationIds.length > 0 && dates.length > 0,
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from("stay_reservation_days")
        .select(DETAIL_SELECT)
        .in("stay_reservation_id", reservationIds)
        .in("stay_date", dates);

      if (error) throw error;

      const details = new Map<string, PlanUsageDetail>();
      for (const row of (data ?? []) as DayRow[]) {
        const detail = normalizePlanUsageDetail(row);
        details.set(planUsageDetailKey(detail.stayReservationId, detail.stayDate), detail);
      }
      return details;
    },
  });
}
